import { EARTH_RADIUS_M } from '@app/consts';

/**
 * Calculate distance between two coordinates using the Haversine formula.
 * @param lat1 - Latitude of the first point in degrees
 * @param lon1 - Longitude of the first point in degrees
 * @param lat2 - Latitude of the second point in degrees
 * @param lon2 - Longitude of the second point in degrees
 * @returns Distance in meters
 */
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

    const lat1Rad = toRadians(lat1);
    const lat2Rad = toRadians(lat2);
    const deltaLat = toRadians(lat2 - lat1);
    const deltaLon = toRadians(lon2 - lon1);

    const a =
        Math.sin(deltaLat / 2) ** 2 +
        Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_M * c; // Distance in meters
}

/**
 * Format distance according to rules:
 * - Above 1km: rounded to nearest km (1400m -> "1 km", 1600m -> "2 km")
 * - Below 1km: rounded to nearest 100m (200m, 300m, etc.)
 * - Below 100m: show as "100 m"
 * @param distanceInMeters - Distance in meters
 * @returns Formatted distance string
 */
export function formatDistance(distanceInMeters: number): string {
    if (distanceInMeters >= 1000) {
        // Round to nearest kilometer
        const km = Math.round(distanceInMeters / 1000);
        return `${km} km`;
    }

    if (distanceInMeters < 100) {
        return '100 m';
    }

    // Round to nearest 100 meters
    const roundedMeters = Math.round(distanceInMeters / 100) * 100;
    return `${roundedMeters} m`;
}
